import clsx from 'clsx'
import find from 'lodash/find'
import React, { FC, useContext, useEffect, useState } from 'react'

import { useAmplitude } from '@Analytics/hook'
import SvgCheck from '@Assets/SVG/Check'
import SvgClubTrendhim from '@Assets/SVG/ClubTrendhim'
import { SmallShippingUrgencyTimer } from '@Components/UrgencyTimer/SmallUrgencyTimer/smallShippingUrgencyTimer'
import { useClub } from '@Domains/Club/Hook'
import { PageType, Product } from '@Generated/graphql'
import { getRTEFieldText } from '@Globals/CMS/FieldHelpers'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { MetadataContext } from '@Globals/Metadata'
import { useCheapestShippingPrice, useFreeShippingThreshold, useFreeShippingThresholdClub } from '@Globals/Metadata/Hooks'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './ProductShipping.module.scss'

const STOCK_REVEAL_LIMIT = 10

interface ProductShippingProps {
  product: Product
}

export const ProductShipping: FC<ProductShippingProps> = ({ product }) => {
  const { t } = useTranslation()
  const cheapestShippingPrice = useCheapestShippingPrice()
  const freeShippingThreshold = useFreeShippingThreshold()
  const freeShippingThresholdClub = useFreeShippingThresholdClub()
  const [showTrendhimClubCTA, setShowTrendhimClubCTA] = useState(false)

  const { bars } = useContext(MetadataContext)
  const openModal = useCMSModalOpen()
  const { isLoggedIn, setModalState } = useClub()
  const ampli = useAmplitude()
  const useExtendedReturnV1 = useSplitTest('extended_return_v1', 'b')

  const urgencyBar = find(bars, bar => bar.type === 'urgency')

  const price = product?.masterVariant?.price?.discount?.value || (product?.masterVariant?.price?.value || 0)

  const shippingIsFree = price >= (freeShippingThreshold?.value || 0)
    || cheapestShippingPrice === null || cheapestShippingPrice?.value === 0

  const shippingInformation = (() => {
    if (urgencyBar) {
      const overrideProductDescription = getRTEFieldText(urgencyBar.fields || [], 'overrideProductDescriptionShippingThresholdText')
      if (overrideProductDescription) {
        return <span dangerouslySetInnerHTML={{ __html: overrideProductDescription }} />
      }
    }
    if (shippingIsFree) {
      if (cheapestShippingPrice?.value === 0) {
        return t('header:FREE_STANDARD_SHIPPING_ALL_ORDERS')
      }
      return t('products:FREE_SHIPPING_ON_THIS_ITEM')
    }

    if (!cheapestShippingPrice || !freeShippingThreshold) {
      return ''
    }

    return t(
      'products:SHIPPING_X_FREE_OVER_X',
      {
        arg1: cheapestShippingPrice.format,
        arg2: freeShippingThreshold.format,
      },
    )
  })()

  const quantity = product?.availability?.quantity || 0
  const showShippingInfo = quantity > 0
  const inStockLabel = quantity > STOCK_REVEAL_LIMIT
    ? t('products:PP_IN_STOCK_LABEL')
    : `${quantity} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`

  useEffect(() => {
    if (!isLoggedIn && freeShippingThresholdClub) {
      setShowTrendhimClubCTA(true)
    } else {
      setShowTrendhimClubCTA(false)
    }
  }, [isLoggedIn, freeShippingThresholdClub])

  const clubFreeShipping = freeShippingThreshold
    && freeShippingThresholdClub
    && price > freeShippingThresholdClub?.value
    && price < freeShippingThreshold?.value

  return (
    <>
      <div className={clsx(styles.shipping, !isLoggedIn && styles.withClubSignup)}>
        <SmallShippingUrgencyTimer
          className={styles.shippingUrgencyTimer}
        />
        <button
          className={styles.shippingModalTrigger}
          onClick={() => openModal(PageType.Modal, '20', 'pdp_below_urgency_timer')}
          type="button"
        >
          {t('shipping:PP_SEE_SHIPPING_AND_RETURN_OPTIONS')}
        </button>

        <div className={styles.usps}>
          {showShippingInfo && (
            <div className={styles.usp}>
              <SvgCheck />
              {inStockLabel}
            &nbsp;&ndash;&nbsp;
              {shippingInformation}
            </div>
          )}
          <div className={styles.usp}>
            <SvgCheck />
            {t('products:RETURN_POLICY_365_DAYS', { context: useExtendedReturnV1 ? 'extended' : undefined })}
          </div>
        </div>

      </div>
      {showTrendhimClubCTA && (
        <div className={styles.clubSignup}>
          <div className={styles.clubSignupText}>
            <SvgClubTrendhim className={styles.clubSignupSvg} />

            <span>
              {clubFreeShipping
                ? t('club:TRENDHIMCLUB_GET_FREE_SHIPPING')
                : t('club:TRENDHIMCLUB_GET_15_PERCENT_CASHBACK')}
            </span>
          </div>
          <button
            className={styles.joinClubButton}
            onClick={() => {
              setModalState('EMAIL_DECISION')
              ampli.promptOpen({ source_id: clubFreeShipping ? 'club_shipping_signup' : 'club_cashback_signup' })
            }}
            type="button"
          >
            {t('club:TRENDHIMCLUB_JOIN_FOR_FREE')}
          </button>
        </div>
      )}
    </>
  )
}
